import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Select, Row, Col, Alert, Typography } from 'antd';
import { MobileOutlined } from '@ant-design/icons';
import { AModal, ATitle } from '../../../../components';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface ICreateEditProps {
  open: boolean;
  close: () => void;
  template: any | null;
  onSuccess: () => void; // ADICIONADO: Para notificar a tabela principal
}

const TemplateCreateEdit: React.FC<ICreateEditProps> = ({
  open,
  close,
  template,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState({
    header: '',
    body: 'A sua mensagem aparecerá aqui...',
    footer: '',
    category: 'MARKETING',
  });

  // ADICIONADO: Limpeza do formulário ao abrir a modal
  useEffect(() => {
    if (open) {
      if (template) {
        const header =
          template.components?.find((c: any) => c.type === 'HEADER')?.text ||
          '';
        const body =
          template.components?.find((c: any) => c.type === 'BODY')?.text || '';
        const footer =
          template.components?.find((c: any) => c.type === 'FOOTER')?.text ||
          '';

        form.setFieldsValue({
          name: template.name,
          category: template.category,
          language: template.language,
          header_text: header,
          body_text: body,
          footer_text: footer,
        });

        setPreview({ header, body, footer, category: template.category });
      } else {
        form.resetFields();
        setPreview({
          header: '',
          body: 'A sua mensagem aparecerá aqui...',
          footer: '',
          category: 'MARKETING',
        });
      }
    }
  }, [open, template, form]);

  const onValuesChange = (_: any, allValues: any) => {
    setPreview({
      header: allValues.header_text || '',
      body: allValues.body_text || 'A sua mensagem aparecerá aqui...',
      footer: allValues.footer_text || '',
      category: allValues.category || 'MARKETING',
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        name: values.name.toLowerCase().replace(/\s/g, '_'),
        category: values.category,
        language: values.language,
        components: [
          ...(values.header_text
            ? [{ type: 'HEADER', format: 'TEXT', text: values.header_text }]
            : []),
          { type: 'BODY', text: values.body_text },
          ...(values.footer_text
            ? [{ type: 'FOOTER', text: values.footer_text }]
            : []),
        ],
      };

      // Se for edição, a API da Meta costuma aceitar POST para o mesmo nome
      // ou deverá ser ajustado para api.put consoante o seu backend.
      if (template?.id) {
        await api.put(`/whatsapp/templates/${template.id}`, payload);
      } else {
        await api.post('/whatsapp/templates', payload);
      }

      addToast({
        type: 'success',
        title: template ? 'Template Atualizado' : 'Template Criado',
        description: 'O template foi enviado para análise da Meta.',
      });
      onSuccess(); // Atualiza a tabela
      close(); // Fecha a modal
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          err.response?.data?.message || 'Verifique os campos obrigatórios.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AModal
      open={open}
      close={close}
      title={
        <ATitle icon={<MobileOutlined />}>
          {template ? 'Editar Template' : 'Novo Template'}
        </ATitle>
      }
      onOk={handleSave}
      confirmLoading={loading}
      width={820}
    >
      <Row gutter={24} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title="Configuração Estrutural"
            bordered={false}
            className="shadow-sm"
          >
            <Form
              form={form}
              layout="vertical"
              onValuesChange={onValuesChange}
              initialValues={{ category: 'MARKETING', language: 'pt_BR' }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Nome Técnico"
                    name="name"
                    rules={[
                      { required: true, message: 'Obrigatório' },
                      {
                        pattern: /^[a-z0-9_]+$/,
                        message: 'Apenas minúsculas e underscores (_)',
                      },
                    ]}
                    tooltip="Apenas minúsculas e underscores (ex: aviso_vencimento)."
                  >
                    <Input
                      placeholder="ex: agendamento_confirmado"
                      disabled={!!template}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Categoria"
                    name="category"
                    rules={[{ required: true, message: 'Obrigatório' }]}
                  >
                    <Select>
                      <Option value="MARKETING">Marketing (Promoções)</Option>
                      <Option value="UTILITY">
                        Utilidade (Avisos/Recibos)
                      </Option>
                      <Option value="AUTHENTICATION">
                        Autenticação (Códigos OTP)
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="Idioma"
                    name="language"
                    rules={[{ required: true, message: 'Obrigatório' }]}
                  >
                    <Select>
                      <Option value="pt_BR">Português (Brasil)</Option>
                      <Option value="pt_PT">Português (Portugal)</Option>
                      <Option value="en_US">Inglês (EUA)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Cabeçalho (Opcional)" name="header_text">
                <Input placeholder="Título em negrito" maxLength={60} />
              </Form.Item>

              <Form.Item
                label="Corpo da Mensagem"
                name="body_text"
                rules={[{ required: true, message: 'Obrigatório' }]}
              >
                <TextArea
                  rows={5}
                  placeholder="Olá {{1}}, a sua consulta é no dia {{2}}."
                />
              </Form.Item>

              <Form.Item label="Rodapé (Opcional)" name="footer_text">
                <Input
                  placeholder="Nome da clínica ou empresa"
                  maxLength={60}
                />
              </Form.Item>

              <Alert
                showIcon
                type="info"
                message="Dica de Variáveis"
                description="Utilize {{1}}, {{2}} para dados que mudam em cada envio, como nome do paciente ou data."
              />
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Title level={5} style={{ textAlign: 'center', marginBottom: 16 }}>
            Pré-visualização
          </Title>
          <div
            style={{
              background: '#e5ddd5',
              padding: '30px 20px',
              borderRadius: '24px',
              border: '8px solid #333',
              maxWidth: '320px',
              margin: '0 auto',
            }}
          >
            <div
              style={{
                background: '#fff',
                padding: '12px',
                borderRadius: '8px',
                boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                position: 'relative',
              }}
            >
              {preview.header && (
                <div
                  style={{
                    fontWeight: 'bold',
                    fontSize: '14px',
                    marginBottom: '4px',
                  }}
                >
                  {preview.header}
                </div>
              )}
              <div
                style={{
                  fontSize: '13px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {preview.body}
              </div>
              {preview.footer && (
                <div
                  style={{
                    fontSize: '11px',
                    color: '#888',
                    marginTop: '6px',
                  }}
                >
                  {preview.footer}
                </div>
              )}
              <div
                style={{
                  textAlign: 'right',
                  fontSize: '10px',
                  color: '#999',
                  marginTop: '4px',
                }}
              >
                15:30
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </AModal>
  );
};

export default TemplateCreateEdit;
